/**
 * Customer-facing signal postmortem — mirrors the internal July 2026
 * postmortem study. Lives at /markets/postmortem. Numbers are pinned to
 * the study window (2026-06-27 → 2026-07-10); a re-run in a later month
 * will land as a dated update. Plain-English only, no formulas, no
 * "engine" language, no promise of future results.
 */
import React, { useEffect } from 'react';
import { Navigation } from '../Navigation';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const BEACON_URL = 'https://icq67h6a7f.execute-api.us-east-2.amazonaws.com/track';
function beacon(page: string) {
  try {
    const source = new URLSearchParams(window.location.search).get('utm_source') || 'organic';
    fetch(BEACON_URL, {
      method: 'POST', mode: 'cors', keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page, source, referer: document.referrer || '' }),
    }).catch(() => {});
  } catch { /* never block render */ }
}

const c = {
  bg: '#0f172a', card: '#1f2937', border: '#374151',
  text: '#f9fafb', muted: '#9ca3af', dim: '#6b7280',
  blue: '#3b82f6', green: '#22c55e', amber: '#f59e0b',
};

// Public-safe header (see SignalTestResults for the mirror version)
const PageHeader: React.FC = () => {
  const { user } = useAuth();
  if (user) return <Navigation />;
  return (
    <nav style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px 24px', borderBottom: `1px solid ${c.border}`, background: '#0b1220',
    }}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: c.text }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 26, height: 26, borderRadius: 4,
          background: c.blue, color: '#fff', fontWeight: 700, fontSize: 14,
        }}>W</span>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Wealth Financial Systems</span>
      </Link>
      <div style={{ display: 'flex', gap: 8 }}>
        <Link to="/login" style={{
          color: c.text, textDecoration: 'none', fontSize: 13, fontWeight: 500,
          padding: '6px 14px', borderRadius: 4, border: `1px solid ${c.border}`,
        }}>Sign in</Link>
        <Link to="/login" style={{
          background: c.blue, color: '#fff', textDecoration: 'none', fontSize: 13, fontWeight: 600,
          padding: '6px 14px', borderRadius: 4,
        }}>Get started</Link>
      </div>
    </nav>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginTop: 28 }}>
    <h2 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700 }}>{title}</h2>
    <div style={{ color: c.muted, fontSize: 14, lineHeight: 1.7 }}>{children}</div>
  </div>
);

const StatTile: React.FC<{
  label: string; value: string; sub: string; accent?: boolean;
}> = ({ label, value, sub, accent }) => (
  <div style={{
    background: c.card,
    border: `1px solid ${accent ? c.amber : c.border}`,
    borderRadius: 8, padding: '16px 18px',
    minWidth: 0,
  }}>
    <div style={{
      color: accent ? c.amber : c.muted, fontSize: 10.5,
      fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase',
    }}>{label}</div>
    <div style={{
      fontSize: 30, fontWeight: 700, marginTop: 6, color: c.text,
    }}>{value}</div>
    <div style={{ color: c.dim, fontSize: 12, marginTop: 4 }}>{sub}</div>
  </div>
);

// Best-exit hour histogram, values from the July 2026 study window.
const HOURS: Array<[string, number]> = [
  ['00:00 UTC',  21],
  ['02:00 UTC',  53],
  ['12:00 UTC',  24],
  ['13:00 UTC', 187],
  ['14:00 UTC', 164],
  ['15:00 UTC',  75],
  ['16:00 UTC', 238],
  ['17:00 UTC', 581],
  ['19:00 UTC', 163],
  ['20:00 UTC',  70],
];
const HOUR_MAX = Math.max(...HOURS.map(([, v]) => v));

const HourBar: React.FC<{ label: string; count: number }> = ({ label, count }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0', fontSize: 12.5, fontFamily: 'ui-monospace, Menlo, monospace' }}>
    <div style={{ width: 92, color: c.muted, whiteSpace: 'nowrap' }}>{label}</div>
    <div style={{ flex: 1, background: '#111827', borderRadius: 3, height: 14, position: 'relative', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0,
        width: `${(count / HOUR_MAX) * 100}%`,
        background: c.amber,
      }} />
    </div>
    <div style={{ width: 50, textAlign: 'right', color: c.text, fontWeight: 500 }}>{count}</div>
  </div>
);

export const SignalPostmortem: React.FC = () => {
  useEffect(() => { beacon('postmortem'); }, []);
  return (
  <div className="dashboard-container" style={{ background: c.bg, minHeight: '100vh', color: c.text }}>
    <PageHeader />
    <div style={{ padding: '24px 32px', maxWidth: 940, margin: '0 auto' }}>
      <div style={{ fontSize: 12, marginBottom: 10 }}>
        <Link to="/" style={{ color: c.muted, textDecoration: 'none' }}>Home</Link>
        <span style={{ color: c.dim, margin: '0 6px' }}>/</span>
        <span style={{ color: c.muted }}>Research</span>
        <span style={{ color: c.dim, margin: '0 6px' }}>/</span>
        <span style={{ color: c.text }}>Pick Postmortem</span>
      </div>

      <div style={{
        color: c.amber, fontSize: 11, fontWeight: 700,
        letterSpacing: '.09em', textTransform: 'uppercase', marginBottom: 6,
      }}>Pick postmortem</div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 6, paddingBottom: 10,
        borderBottom: `2px solid ${c.amber}`,
      }}>
        <span style={{ width: 8, height: 28, borderRadius: 2, background: c.amber }} />
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700 }}>What Notches picks actually did in the market</h1>
      </div>
      <div style={{ color: c.dim, fontSize: 12, marginTop: 6, fontFamily: 'ui-monospace, Menlo, monospace' }}>
        Study window 2026-06-27 → 2026-07-10 · 1,590 top-3 picks replayed hour-by-hour against archived option chains · 1 contract per pick, no fees.
      </div>

      <p style={{ marginTop: 22, fontSize: 15, lineHeight: 1.7, color: c.text }}>
        This is a look at every top-3 pick we surfaced over a two-week window,
        walked forward hour-by-hour through the actual option chain we archive.
        The "best-exit" number is the <b>peak of each trade's trajectory</b> —
        the ceiling a perfect closer would have hit. Real close timing will
        fall short of the ceiling; that gap is the price of not being able to
        see the future.
      </p>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 14, marginTop: 24,
      }}>
        <StatTile label="Picks analyzed"       value="1,590" sub="every top-3 pick, 14-day window" />
        <StatTile label="Positive best-exit"    value="94%"   sub="1,497 of 1,590 hit a green mark" />
        <StatTile label="Median best-exit P&L"  value="$296"  sub="per contract, ceiling of trajectory" />
        <StatTile label="Dominant exit hour"    value="17:00 UTC" sub="581 of 1,590 peaked here (~13:00 ET)" accent />
      </div>

      <Section title="When did the trades peak?">
        <p>
          Which hour of the day the trajectory reached its best-exit point.
          Concentration on one bar = a consistent time-of-day pattern.
        </p>
        <div style={{
          background: c.card, border: `1px solid ${c.border}`,
          borderRadius: 8, padding: '14px 18px', marginTop: 12,
        }}>
          {HOURS.map(([lbl, n]) => <HourBar key={lbl} label={lbl} count={n} />)}
        </div>
        <p style={{ marginTop: 12 }}>
          The heavy 17:00 UTC bar is the close-of-cash-session read (about
          1:00 PM ET). It shows that many trajectories peak into the U.S.
          equity close — which is when a lot of theta gets paid on the puts
          we sell. The next-biggest bars sit around 13:00–16:00 UTC — the
          U.S. open and mid-morning window.
        </p>
        <p>
          The practical read: <b>on many days, the best time to close is not
          the day the pick is entered.</b> If a trade is up meaningfully and
          the calendar is near a favorable close-of-session hour, you have
          historical support for taking it.
        </p>
      </Section>

      <Section title="Reading the winners number">
        <p>
          94% is a ceiling — it counts a trade as a "positive best-exit" if
          the trajectory ever went green during its window. That is not the
          same thing as your actual close. What it tells you: the ranker
          surfaces trades that <b>reliably show a green print at some point
          in their lifetime</b>. The lift you actually capture depends on
          your close discipline and on whether the market handed you an
          opportunity to close near the peak.
        </p>
        <p>
          Median $296 per contract is the middle of the distribution — half
          of the trajectories peaked above this level, half below. There is
          a long right tail (a few picks peaked deep into the hundreds); a
          few also barely touched positive before pulling back. The median
          is the honest midpoint.
        </p>
      </Section>

      <Section title="What this does not tell you">
        <p>
          Best-exit is a <em>ceiling</em>, not a promise. In the same window,
          our actual live-close realized P&amp;L was materially smaller. The
          gap between the ceiling and what a real closer earns is the
          practical cost of not being able to see the future.
        </p>
        <p>
          The study also does not weight by size — every pick counts as one
          contract. Your own sizing, order fills, and exit timing will move
          the number in either direction.
        </p>
      </Section>

      <div style={{ marginTop: 40, padding: '14px 18px', background: c.card, border: `1px solid ${c.border}`, borderLeft: `3px solid ${c.amber}`, borderRadius: 8 }}>
        <div style={{ color: c.amber, fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>Reminder</div>
        <div style={{ fontSize: 13.5, color: c.muted, lineHeight: 1.6 }}>
          Notches picks are information to inform your own trade decisions.
          They are not a recommendation to open, close, or hold any position.
          Past best-exit ceilings do not guarantee future results.
        </div>
      </div>

      <div style={{ marginTop: 24, textAlign: 'center', display: 'flex', gap: 12, justifyContent: 'center' }}>
        <Link to="/markets/test-results" style={{
          display: 'inline-block',
          background: 'transparent', color: c.green,
          border: `1px solid ${c.green}`, padding: '8px 18px', borderRadius: 4,
          fontSize: 13, fontWeight: 600, textDecoration: 'none',
        }}>Research Quality Study →</Link>
        <Link to="/notches/dashboard" style={{
          display: 'inline-block',
          background: 'transparent', color: c.blue,
          border: `1px solid ${c.blue}`, padding: '8px 18px', borderRadius: 4,
          fontSize: 13, fontWeight: 600, textDecoration: 'none',
        }}>Back to Dashboard</Link>
      </div>
    </div>
  </div>
  );
};

export default SignalPostmortem;
